import { useCallback, useEffect, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { collection, doc, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../../../components/firebase";

// IMPORTANT: must match backend callable region
const FUNCTIONS_REGION = "europe-west3";

export function useStreamSignal() {
  const [eventId, setEventId] = useState(null);
  const [status, setStatus] = useState("idle"); // idle|queued|running|done|error
  const [eventDoc, setEventDoc] = useState(null);
  const [messages, setMessages] = useState([]);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);

  const start = useCallback(async ({ type, symbol, tf, riskMode, context }) => {
    setError(null);
    setMessages([]);
    setEventDoc(null);
    setStatus("idle");
    setEventId(null);

    setStarting(true);
    try {
      const functions = getFunctions(undefined, FUNCTIONS_REGION);
      const fn = httpsCallable(functions, "startStreamEvent");

      const res = await fn({ type, symbol, tf, riskMode, context });

      const id = res?.data?.eventId || null;
      if (!id) throw new Error("No eventId returned");

      setEventId(id);
      setStatus("queued");
      return id;
    } catch (e) {
      setError(e);
      setStatus("error");
      return null;
    } finally {
      setStarting(false);
    }
  }, []);

  // subscribe event doc
  useEffect(() => {
    if (!eventId) return;

    const ref = doc(db, "streamEvents", eventId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) return;
        const data = snap.data() || {};
        setEventDoc({ id: snap.id, ...data });
        setStatus(String(data.status || "running"));
        if (data?.error?.message) setError(new Error(String(data.error.message)));
      },
      (err) => {
        setError(err);
        setStatus("error");
      }
    );

    return () => unsub();
  }, [eventId]);

  // subscribe messages
  useEffect(() => {
    if (!eventId) return;

    const qRef = query(
      collection(db, "streamEvents", eventId, "messages"),
      orderBy("createdAt", "asc"),
      limit(120)
    );

    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const arr = [];
        snap.forEach((d) => arr.push({ id: d.id, ...(d.data() || {}) }));
        setMessages(arr);
      },
      (err) => setError(err)
    );

    return () => unsub();
  }, [eventId]);

  const reset = useCallback(() => {
    setEventId(null);
    setStatus("idle");
    setEventDoc(null);
    setMessages([]);
    setStarting(false);
    setError(null);
  }, []);

  return { start, reset, starting, status, eventId, eventDoc, messages, error };
}
